/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect, useLayoutEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { Button } from '@marriott/mi-ui-library';
import { PageParamsContext } from '../../../../modules/context/PageContext';
import { AEMInternetBarProps, InternetBarExperienceProps, InternetBarStateTypes } from '../../InternetBar.types';
import { inStayMockResponse } from '../../__mock__/InternetBar.model';
import { INTERNET_BAR_INITIAL_STATE } from '../../utils/constant';
import { getInternetBarStateSpecificClasses, StateCSS } from '../../internetBar.helper';
import {
  internetBarIcons,
  userExperienceConstants,
  analyticsTrackingIdentifier,
  analyticsTrackingUniqueDescription,
  getInternetBarTrackingIdentifier,
} from '../../../../modules/utils/constants/constants';
import {
  connectBtnTrigger,
  getLoyaltyEnabledForConferenceZone,
  getSFRoomLastNameValidationFlag,
} from '../../utils/handler';
import { serviceStore, useServiceStore } from '../../../../modules/store/serviceStore';
import { getPpv5LinkTarget, updateDataLayerProperties, sanitize } from '../../../../modules/utils/common';
import { useSession } from '@marriott/mi-headless-utils';

export const InternetBarConferenceAgeExperience: FC<InternetBarExperienceProps> = (
  pageProps: InternetBarExperienceProps
) => {
  const { preProcessorResponse } = useContext(PageParamsContext);

  const {
    experienceSegment = '',
    state = '',
    userSegment = '',
    enableLoyaltyConference = false as boolean,
    loyaltyFlip = false,
    enrollmentSourceCodeMapping,
    enableLoyalty = false,
    enableLoyaltyPostConnect = false,
    enableLoyaltyConferencePostConnect = false,
    propertyInfo,
    cna = false,
    sameTab = false,
  } = preProcessorResponse || {};

  const serviceData = useServiceStore((state: serviceStore) => state.serviceData);

  const { isACDLenabled } = useSession();
  const isLoyaltyEnabledForConference = getLoyaltyEnabledForConferenceZone(
    enableLoyaltyConference ? enrollmentSourceCodeMapping?.loyalty : enrollmentSourceCodeMapping?.nonloyalty,
    propertyInfo?.data?.basicInformation?.globalDivision,
    propertyInfo?.data?.contactInformation?.address?.country?.code
  );
  const { userExperience = '' } = pageProps?.internetPricingData?.data ?? {};
  const {
    currentStateMapping = {
      ...(pageProps?.isAuthorMode ? inStayMockResponse : (INTERNET_BAR_INITIAL_STATE as InternetBarStateTypes)),
    },
  } = pageProps;

  const stateCSS: StateCSS = getInternetBarStateSpecificClasses(
    userSegment,
    state,
    experienceSegment,
    enableLoyaltyConference
  );

  const [isNoLoginInterfaceModal] = useState<boolean>(currentStateMapping?.noLoginInterfaceModal ?? false);

  const [showIcon] = useState<boolean>(
    internetBarIcons && internetBarIcons?.[state] ? internetBarIcons?.[state]?.showIcon : false
  );

  const termsAndConditionsText = sanitize(
    (
      currentStateMapping?.termsAndConditions &&
      pageProps?.[currentStateMapping?.termsAndConditions as keyof AEMInternetBarProps]
    )?.toString() || ''
  );

  const isWelcomeTitle: boolean = currentStateMapping && currentStateMapping?.title === 'welcomeTitle';

  const [loyaltyJoinNow, setLoyaltyJoinNow] = useState<string>(pageProps?.joinButtonLink);
  const [isLoyaltyConnectNowBtnLoading, setLoyaltyConnectNowBtnLoading] = useState<boolean>(false);
  const [loyaltyJoinNowDescriptionLabel, setLoyaltyJoinNowDescriptionLabel] = useState<string>(
    pageProps?.loyaltyJoinNowConferenceDescription
  );

  const termsAndConditionsRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const { Z, zoneMapping } = pageProps?.internetBarCurrentStateData ?? {};
    if (pageProps && pageProps?.internetPricingData) {
      const loyaltyEnrollmentSourceCode = pageProps?.internetPricingData?.['data']?.enrollmentSourceCode
        ? pageProps?.internetPricingData?.['data']?.enrollmentSourceCode
        : pageProps?.internetBarCurrentStateData['loyaltyEnrollmentSourceCode'];
      const joinNowLink = pageProps?.handleLinks(loyaltyEnrollmentSourceCode, zoneMapping, Z);
      setLoyaltyJoinNow(joinNowLink);
    } else if (
      pageProps?.internetBarCurrentStateData &&
      pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode']
    ) {
      const joinNowLink = pageProps?.handleLinks(
        pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode'],
        zoneMapping,
        Z
      );
      setLoyaltyJoinNow(joinNowLink);
    }
  }, [pageProps]);

  useEffect(() => {
    if (pageProps && pageProps?.internetPricingData && pageProps?.internetPricingData?.data) {
      const { enrollmentSourceCode = '', userExperience = '' } = pageProps?.internetPricingData?.data ?? {};
      if (!isACDLenabled) {
        const dataLayer = (window && window?.dataLayer) ?? {};
        updateDataLayerProperties(dataLayer, userExperience, enrollmentSourceCode);
      }
    }
  }, [pageProps?.internetPricingData?.data, isACDLenabled]);

  useEffect(() => {
    if (
      (userExperience && userExperience?.toLowerCase() === userExperienceConstants.conservativeWithIncentive) ||
      userExperience?.toLowerCase() === userExperienceConstants.marketingWithIncentive
    ) {
      setLoyaltyJoinNowDescriptionLabel(pageProps?.loyaltyJoinNowConferenceIncentiveDescription);
    }
  }, [userExperience]);

  useEffect(() => {
    if (termsAndConditionsRef && termsAndConditionsRef?.current) {
      const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData ?? {};
      const propertyPricingTier = zoneMapping?.[Z ?? 'GUEST'];
      const termsAndConditionsLinks = termsAndConditionsRef.current.getElementsByTagName('a');
      if (termsAndConditionsLinks.length > 0) {
        const termsAndConditionsLink = termsAndConditionsLinks[0];
        termsAndConditionsLink.classList.add('custom_click_track');
        termsAndConditionsLink?.setAttribute(
          'data-custom_click_track_value',
          `${getInternetBarTrackingIdentifier(state, analyticsTrackingIdentifier?.internetBar, propertyPricingTier)}|${
            analyticsTrackingUniqueDescription.termsOfUse
          }|internal|`
        );
      }
    }
  }, [termsAndConditionsText, pageProps?.internetBarCurrentStateData]);

  useLayoutEffect(() => {
    pageProps?.resizeImage && pageProps?.resizeImage();
  });

  const handleBtnClick = () => {
    const { setCurrentStateHasModalOption, setModalType, setShowInternetBarModal } = pageProps;
    const internetbarInitialValue = { ...pageProps?.internetBarCurrentStateData };
    const localRequirement = internetbarInitialValue?.['LR']?.toUpperCase() ?? '';

    if (localRequirement && localRequirement !== 'TCTC') {
      if (localRequirement === 'AF_AO' || localRequirement === 'STRICT_AO') {
        setModalType('ACCESS');
        setShowInternetBarModal(true);
        setCurrentStateHasModalOption(true);
      } else {
        setModalType('LOGIN');
        setShowInternetBarModal(true);
        connectBtnTrigger(isNoLoginInterfaceModal, { ...internetbarInitialValue });
      }
    } else {
      if (isNoLoginInterfaceModal) {
        setModalType('');
        setShowInternetBarModal(false);
        setLoyaltyConnectNowBtnLoading(true);
        connectBtnTrigger(true, { ...internetbarInitialValue });
      } else {
        const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData || {};
        const isSFRoomLastNameValidationFlag = getSFRoomLastNameValidationFlag(
          preProcessorResponse &&
            (enableLoyalty ||
            enableLoyaltyPostConnect ||
            ((enableLoyaltyConference || enableLoyaltyConferencePostConnect) && isLoyaltyEnabledForConference)
              ? enrollmentSourceCodeMapping?.loyalty
              : enrollmentSourceCodeMapping?.nonloyalty),
          propertyInfo?.data?.basicInformation?.globalDivision,
          propertyInfo?.data?.contactInformation?.address?.country?.code
        );
        if (!isSFRoomLastNameValidationFlag && !localRequirement && zoneMapping?.[Z ?? ''] === 'SF') {
          connectBtnTrigger(true, { ...internetbarInitialValue });
        } else {
          setModalType('LOGIN');
          setShowInternetBarModal(true);
          connectBtnTrigger(isNoLoginInterfaceModal, { ...internetbarInitialValue });
        }
      }
    }
  };

  useEffect(() => {
    if (serviceData?.connectBtnClick) {
      handleBtnClick();
    }
  }, [serviceData?.connectBtnClick]);

  return currentStateMapping && pageProps ? (
    <div
      className={clsx(
        `container internet-bar-content ${
          enableLoyaltyConference && isLoyaltyEnabledForConference
            ? `internet-bar-content-${state}-loyalty`
            : `internet-bar-content-${state}`
        }`,
        `${stateCSS?.internetBarContent}`
      )}
    >
      <div
        className={clsx(
          'col-12 d-flex flex-column justify-content-center px-0 text-center align-items-center',
          `${stateCSS?.internetBarContentContainer}`
        )}
      >
        {/* 1. Internet Bar Icon Configuration */}
        {showIcon && (
          <span
            className={clsx(
              `t-accent-color d-inline-block ${internetBarIcons?.[state]?.iconName}`,
              `${
                isLoyaltyEnabledForConference
                  ? `icon-xxl mt-3 mt-xl-3 mb-3`
                  : `internet-bar-content__icon internet-bar-content-${state}__icon`
              } ${stateCSS?.internetBarIcon}`
            )}
            data-testid="internet-bar-icon"
          ></span>
        )}
        {/* 2. Title */}
        {isWelcomeTitle ? (
          <>
            <h3
              className={`mt-4 mb-3 pt-xl-4 internet-bar-content__title internet-bar-content-${state}__title`}
              data-testid="internet-bar-welcome-title"
            >
              {pageProps?.[currentStateMapping?.title as keyof AEMInternetBarProps] || ''}
            </h3>
            {/* Separator */}
            <div
              className={`t-accent-color mb-3 mb-xl-4 internet-bar-content__separator internet-bar-content-${state}__separator`}
            ></div>
          </>
        ) : (
          <h3
            className={clsx('col-12 internet-bar-content__title', `${stateCSS?.internetBarNotWelcomeTitle}`)}
            data-testid="internet-bar-welcome-title"
          >
            {pageProps?.[currentStateMapping?.title as keyof AEMInternetBarProps] ?? ''}
          </h3>
        )}

        <div className={clsx('loyalty-container', `${stateCSS?.internetBarLoyaltyContainer}`)}>
          <div
            className={clsx(
              'd-flex mb-5',
              `${loyaltyFlip ? 'flex-column-reverse flex-md-row-reverse' : 'flex-column flex-md-row'}`
            )}
          >
            <div className="loyalty-container-card1 col-12 col-md-6 p-0 d-flex flex-column justify-content-between align-items-center">
              <div className={clsx('d-flex flex-column', `${loyaltyFlip ? 'pt-5 pt-md-0' : ''}`)}>
                <p
                  className={clsx(
                    't-font-s mb-0 loyalty-description t-font-weight-m pb-2',
                    `${stateCSS?.internetBarLoyaltyDescription}`
                  )}
                >
                  {pageProps?.loyaltyJoinNowConferenceTitle}
                </p>
                <p className={clsx('t-font-s', `${stateCSS?.internetBarLoyaltySubDescription}`)}>
                  {loyaltyJoinNowDescriptionLabel}
                </p>
              </div>
              <Button
                className={clsx(
                  'm-button-primary pt-2 loyalty-buttons',
                  `${stateCSS?.internetBarLoyaltyButtons} d-flex justify-content-center`,
                  `${!pageProps?.enableLoyaltyJoinNowBtn ? 'disabled' : ''}`
                )}
                isLink={true}
                href={loyaltyJoinNow}
                trackingProperties={pageProps?.trackingProperties}
                target={getPpv5LinkTarget(state, sameTab, cna, pageProps?.joinButtonLinkInNewTab)}
                custom_click_track_value={`${getInternetBarTrackingIdentifier(
                  state,
                  analyticsTrackingIdentifier.internetBar,
                  pageProps?.internetBarCurrentStateData?.['zoneMapping']?.[
                    pageProps?.internetBarCurrentStateData?.['Z'] ?? 'GUEST'
                  ]
                )}|${analyticsTrackingUniqueDescription.joinNowLink}|internal`}
                data-custom_click_track_value={`${getInternetBarTrackingIdentifier(
                  state,
                  analyticsTrackingIdentifier.internetBar,
                  pageProps?.internetBarCurrentStateData?.['zoneMapping']?.[
                    pageProps?.internetBarCurrentStateData?.['Z'] ?? 'GUEST'
                  ]
                )}|${analyticsTrackingUniqueDescription.joinNowLink}|internal`}
                data-is-click-track={true}
                ariaLabel={pageProps?.joinButtonLabel}
                isDisabled={pageProps?.isAuthorMode ? false : !pageProps?.enableLoyaltyJoinNowBtn}
              >
                {!pageProps?.enableLoyaltyJoinNowBtn ? (
                  <div className="m-spinner-s" data-testid="activate-spinner"></div>
                ) : (
                  <span>{pageProps?.joinButtonLabel}</span>
                )}
              </Button>
            </div>
            <div className="loyalty-container-card2 col-12 col-md-6 d-flex flex-column justify-content-between align-items-center">
              <div
                className={`${
                  loyaltyFlip ? '' : 'd-flex flex-column justify-content-between align-items-center flex-1 pt-5 pt-md-0'
                }`}
              >
                <p
                  className={clsx(
                    't-font-s mb-0 loyalty-description t-font-weight-m pb-2',
                    `${stateCSS?.internetBarLoyaltyDescription}`
                  )}
                >
                  {pageProps?.loyaltyConnectConferenceTitle}
                </p>
                <p className={clsx('t-font-s', `${stateCSS?.internetBarLoyaltySubDescription}`)}>
                  {pageProps?.loyaltyConnectConferenceDescription}
                </p>
                <Button
                  className={clsx(
                    `${isLoyaltyEnabledForConference ? `m-button-secondary` : `m-button-primary`}`,
                    `loyalty-buttons`,
                    'm-button-primary loyalty-buttons',
                    `${stateCSS?.internetBarLoyaltyButtons}`,
                    `${isLoyaltyConnectNowBtnLoading ? 'disabled' : ''}`
                  )}
                  isDisabled={isLoyaltyConnectNowBtnLoading}
                  trackingProperties={pageProps?.trackingProperties}
                  data-custom_click_track_value={`${getInternetBarTrackingIdentifier(
                    state,
                    analyticsTrackingIdentifier.internetBar,
                    pageProps?.internetBarCurrentStateData?.['zoneMapping']?.[
                      pageProps?.internetBarCurrentStateData?.['Z'] ?? 'GUEST'
                    ]
                  )}|${analyticsTrackingUniqueDescription.loginModelLoyaltyLoginConnectNowButton}|external`}
                  custom_click_track_value={`${getInternetBarTrackingIdentifier(
                    state,
                    analyticsTrackingIdentifier.internetBar,
                    pageProps?.internetBarCurrentStateData?.['zoneMapping']?.[
                      pageProps?.internetBarCurrentStateData?.['Z'] ?? 'GUEST'
                    ]
                  )}|${analyticsTrackingUniqueDescription.loginModelLoyaltyLoginConnectNowButton}|external`}
                  data-is-click-track={true}
                  ariaLabel={pageProps?.loyaltyConnectNowButton}
                  callback={() => {
                    handleBtnClick();
                  }}
                >
                  {isLoyaltyConnectNowBtnLoading ? (
                    <div className="m-spinner-s" data-testid="activate-spinner"></div>
                  ) : (
                    <span>{pageProps?.loyaltyConnectNowButton}</span>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {isLoyaltyEnabledForConference && <div className={`t-accent-color mt-4 loyalty-container__separator`}></div>}

          {currentStateMapping?.termsAndConditions && (
            <p
              ref={termsAndConditionsRef}
              className={clsx(
                `mb-0 t-font-xs internet-bar-content__footer-container__tnc-text`,
                `${isLoyaltyEnabledForConference ? `internet-bar-content__footer-container__tnc-text-conference` : ``}`,
                `${stateCSS?.internetBarTermsAndConditions}`
              )}
              data-testid="internet-bar-terms-condition"
              dangerouslySetInnerHTML={{
                __html: termsAndConditionsText,
              }}
            ></p>
          )}
        </div>
      </div>
    </div>
  ) : null;
};
